import {Injectable} from "@angular/core";
import {ReportError} from "app/app.service";

import {ApiService} from "app/services/domain/api.service";
import {AutoBatcher, BaseApplicationComponent, CachedFetcher} from "app/services/domain/base.service";
import type {AppDomainContext} from "app/services/domain/domain.module";
import {WafAssessmentDefinitionExtended} from "app/services/domain/waf-assessment-definitions.service";
import {WafAssessmentDefinitionVersionExtended, WafAssessmentVersionsService} from "app/services/domain/waf-assessment-versions.service";
import {WellArchitectedFrameworkBestPracticeExtended, WellArchitectedFrameworkExtended, WellArchitectedFrameworkPillarExtended} from "app/services/domain/waf-frameworks.service";
import * as Models from "app/services/proxy/model/models";

import {LocalStorageCacheStore} from "framework/services/cache.service";
import {Lookup, LookupHelpers, UtilsService} from "framework/services/utils.service";
import {MomentHelper} from "framework/ui/formatting/date-format.pipe";
import {inParallel, mapInParallel} from "framework/utils/concurrency";

@Injectable()
export class WafAssessmentRunService
{
    private m_fetcher = new CachedFetcher(new LocalStorageCacheStore("WAF_ASSESSMENT_RUNS"));

    private m_assessmentRunBatcher = new AssessmentBatcher((ids) => this.m_api.wafAssessmentRunsApi.list(Models.WellArchitectedFrameworkAssessmentRunRequest.newInstance({assessment_definition_ids: ids})),
                                                           (ids) => this.batchRuns(ids),
                                                           async (run) =>
                                                           {
                                                               const version = await this.m_assessmentVersion.getExtendedByIdentity(run.assessment_version);
                                                               return version.model.definition.sysId;
                                                           });

    private m_historyBatcher = new AssessmentBatcher((ids) => this.m_api.wafAssessmentRunsApi.historyList(Models.WellArchitectedFrameworkAssessmentRunHistoryRequest.newInstance({assessment_run_ids: ids})),
                                                     (ids) =>
                                                     {
                                                         return this.m_fetcher.batch<Models.WellArchitectedFrameworkAssessmentRunHistory>(ids, m =>
                                                         {
                                                             Models.WellArchitectedFrameworkAssessmentRunHistory.fixupPrototype(m);
                                                             return m.updatedOn;
                                                         }, (req) => this.m_api.wafAssessmentRunsApi.historyBatch(req));
                                                     },
                                                     async (history) => history.assessment_run.sysId);

    private m_questionBatcher = new AssessmentBatcher((ids) => this.m_api.wafAssessmentRunsApi.questionList(Models.WellArchitectedFrameworkAssessmentRunQuestionRequest.newInstance({assessment_run_history_ids: ids})),
                                                      (ids) =>
                                                      {
                                                          return this.m_fetcher.batch<Models.WellArchitectedFrameworkAssessmentRunQuestion>(ids, m =>
                                                          {
                                                              Models.WellArchitectedFrameworkAssessmentRunQuestion.fixupPrototype(m);
                                                              return m.updatedOn;
                                                          }, (req) => this.m_api.wafAssessmentRunsApi.questionBatch(req));
                                                      },
                                                      async (question) => question.assessment_run_history.sysId);

    constructor(private m_api: ApiService,
                private m_assessmentVersion: WafAssessmentVersionsService)
    {}

    public createRun(assessmentVersion: Models.RecordIdentity,
                     workloadVersion: Models.RecordIdentity): Promise<Models.WellArchitectedFrameworkAssessmentRun>
    {
        const request = Models.WellArchitectedFrameworkAssessmentRunCreateRequest.newInstance({
                                                                                                  assessment_version: assessmentVersion,
                                                                                                  workload_version  : workloadVersion
                                                                                              });
        return this.m_api.wafAssessmentRunsApi.create(request);
    }

    @ReportError
    public setState(assessmentRunId: string,
                    status: Models.WellArchitectedFrameworkAssessmentRunStatus): Promise<Models.WellArchitectedFrameworkAssessmentRun>
    {
        const request = Models.WellArchitectedFrameworkAssessmentRunSetStateRequest.newInstance({status: status});
        return this.m_api.wafAssessmentRunsApi.setState(assessmentRunId, request);
    }

    public listAllRuns(assessmentId: string): Promise<Models.WellArchitectedFrameworkAssessmentRun[]>
    {
        return this.m_assessmentRunBatcher.get(assessmentId);
    }

    public batchRuns(assessmentRunRecords: Models.RecordIdentity[]): Promise<Models.WellArchitectedFrameworkAssessmentRun[]>
    {
        return this.m_fetcher.batch<Models.WellArchitectedFrameworkAssessmentRun>(assessmentRunRecords, m =>
        {
            Models.WellArchitectedFrameworkAssessmentRun.fixupPrototype(m);
            return m.updatedOn;
        }, (req) => this.m_api.wafAssessmentRunsApi.batch(req));
    }

    public triggerScan(assessmentRunId: string,
                       context: Models.WellArchitectedFrameworkAssessmentContext): Promise<Models.RecordIdentity>
    {
        return this.m_api.wafAssessmentRunsApi.scan(assessmentRunId, context);
    }

    @ReportError
    public createMilestone(assessmentRunId: string,
                           milestoneName: string): Promise<Models.RecordIdentity>
    {
        return this.m_api.wafAssessmentRunsApi.milestone(assessmentRunId, Models.WellArchitectedAssessmentRunCreateMilestoneRequest.newInstance({name: milestoneName}));
    }

    public async loadAllHistory(assessmentRunId: string): Promise<WafAssessmentRunHistoryExtended[]>
    {
        const records = await this.m_historyBatcher.get(assessmentRunId);

        if (!records) return null;
        if (!records.length) return [];

        return this.loadHistory(records);
    }

    public async loadHistory(histories: Models.WellArchitectedFrameworkAssessmentRunHistory[]): Promise<WafAssessmentRunHistoryExtended[]>
    {
        const allQuestions = await mapInParallel(histories, async history => this.m_questionBatcher.get(history.sysId));

        return UtilsService.zipArrays(histories, allQuestions)
                           .map(([history, questions]) => new WafAssessmentRunHistoryExtended(history, questions))
                           .sort((entryA,
                                  entryB) => MomentHelper.compareDates(entryA.model.createdOn, entryB.model.createdOn, true));
    }

    public answer(assessmentRunId: string,
                  answerRequest: Models.WellArchitectedFrameworkAssessmentRunAnswerRequest): Promise<Models.WellArchitectedFrameworkAssessmentRunQuestion>
    {
        return this.m_api.wafAssessmentRunsApi.answer(assessmentRunId, answerRequest);
    }
}

class AssessmentBatcher<T> extends AutoBatcher<T[]>
{
    constructor(list: (ids: Models.RecordIdentity[]) => Promise<Models.RecordIdentity[]>,
                batch: (ids: Models.RecordIdentity[]) => Promise<T[]>,
                discriminator: (model: T) => Promise<string>)
    {
        const helper = new AssessmentBatcherHelper<T>(batch);
        super(100, async (ids) =>
        {
            const records      = await list(ids);
            const modelLookup: Lookup<T[]> = {};

            if (records?.length)
            {
                await inParallel(records, async (record) =>
                {
                    const model = await helper.getByIdentity(record);
                    const key   = await discriminator(model);
                    LookupHelpers.pushToArray(modelLookup, key, model);
                });
            }

            return ids.map(id => modelLookup[id.sysId]);
        });
    }
}

class AssessmentBatcherHelper<T> extends AutoBatcher<T>
{
    constructor(batch: (ids: Models.RecordIdentity[]) => Promise<T[]>)
    {
        super(100, batch);
    }
}

export class WafAssessmentRunQuestionExtended
{
    get id(): string
    {
        return this.model.sysId;
    }

    private m_model: Models.WellArchitectedFrameworkAssessmentRunQuestion;
    get model(): Models.WellArchitectedFrameworkAssessmentRunQuestion
    {
        return this.m_model;
    }

    constructor(model: Models.WellArchitectedFrameworkAssessmentRunQuestion)
    {
        this.updateModel(model);
    }

    updateIfMoreRecent(other: WafAssessmentRunQuestionExtended)
    {
        if (MomentHelper.compareDates(this.m_model.updatedOn, other.m_model.updatedOn, true) < 0)
        {
            this.updateModel(other.m_model);
        }
    }

    private updateModel(model: Models.WellArchitectedFrameworkAssessmentRunQuestion)
    {
        this.m_model = Models.WellArchitectedFrameworkAssessmentRunQuestion.newInstance(model);
        this.m_model.answers.sort((answerA,
                                   answerB) => MomentHelper.compareDates(answerA.createdOn, answerB.createdOn, true));
    }
}

export class WafAssessmentRunPolicyScanExtended
{
    get id(): string
    {
        return this.model ? `${this.model.policy_uri}/${this.model.cloud_account_id}/${this.contextId}` : this.contextId;
    }

    get policyUri(): string
    {
        return this.model.policy_uri;
    }

    get violatedResourceCount(): number
    {
        return this.model.violated_resource_count;
    }

    public readonly contextId: string;

    public jobExecutionOutputDetails: Models.JobExecutionDetailsResponse;

    public violationsLoading: boolean = true;

    private m_violations: string[];
    get violations(): string[]
    {
        return this.m_violations;
    }

    set violations(ids: string[])
    {
        this.m_violations = ids;
    }

    get context(): Models.WellArchitectedFrameworkAssessmentContext
    {
        return this.model.context;
    }

    get isFailure(): boolean
    {
        return !!this.model.failure;
    }

    get hasViolations(): boolean
    {
        return !this.isFailure && !!this.model.violated_resource_count;
    }

    constructor(public readonly assessmentRunHistoryId: string,
                public readonly model: Models.WellArchitectedFrameworkAssessmentRunPolicyScan)
    {
        this.contextId = WafAssessmentRunPolicyScanExtended.computeContextId(this.context);
    }

    public matchesPillar(framework: WellArchitectedFrameworkExtended,
                         pillar: Models.WellArchitectedFrameworkPillar): boolean
    {
        return this.context.framework_id == framework.model.framework_id &&
               this.context.pillar_id == pillar.pillar_id;
    }

    public matchesQuestion(question: Models.WellArchitectedFrameworkQuestion): boolean
    {
        return this.context.framework_id == question.framework_id &&
               this.context.pillar_id == question.pillar_id &&
               this.context.question_id == question.question_id;
    }

    public matchesBestPractice(bestPractice: Models.WellArchitectedFrameworkBestPractice): boolean
    {
        return this.context.framework_id == bestPractice.framework_id &&
               this.context.pillar_id == bestPractice.pillar_id &&
               this.context.question_id == bestPractice.question_id &&
               this.context.best_practice_id == bestPractice.best_practice_id;
    }

    public async ensureViolations(host: BaseApplicationComponent,
                                  loadFromJobDetails                            = false,
                                  jobOutput: Models.JobExecutionOutputDetails[] = [])
    {
        // to avoid flickering previous resources when switching between issue policies
        this.violationsLoading = true;
        host.detectChanges();

        if (!this.m_violations)
        {
            const request     = Models.WellArchitectedFrameworkAssessmentRunViolationRequest.newInstance({policy_job_id: this.model.policy_job_id});
            const violations  = await host.app.domain.apis.wafAssessmentRunsApi.violationsList(this.assessmentRunHistoryId, request);
            this.m_violations = violations.map((record) => record.sysId);
        }

        if (loadFromJobDetails)
        {
            this.m_violations = jobOutput.map(resource => resource.resource_id);
        }

        this.violationsLoading = false;
    }

    public collectAssociatedBestPractices(pillars: WellArchitectedFrameworkPillarExtended[]): WellArchitectedFrameworkBestPracticeExtended[]
    {
        const associatedBPs: WellArchitectedFrameworkBestPracticeExtended[] = [];

        for (const pillar of pillars)
        {
            for (const question of pillar.questions)
            {
                for (const bp of question.bestPractices)
                {
                    if (this.matchesBestPractice(bp.model) || bp.model.policy_uris?.includes(this.model.policy_uri))
                    {
                        associatedBPs.push(bp);
                    }
                }
            }
        }

        return associatedBPs;
    }

    public shouldShowInReport(pillars: WellArchitectedFrameworkPillarExtended[]): boolean
    {
        const associatedBPs = this.collectAssociatedBestPractices(pillars);

        if (!associatedBPs.length)
        {
            return true;
        }

        return associatedBPs.some(bp =>
                                      bp.questionExt?.applicable &&
                                      bp.questionExt?.status !== Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException &&
                                      bp.status !== Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable &&
                                      bp.status !== Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException);
    }

    public static computeContextId(context: Models.WellArchitectedFrameworkAssessmentContext): string
    {
        if (!context) return null;

        let contextId = "";
        if (context.framework_id) contextId = context.framework_id;
        if (context.pillar_id) contextId += "/" + context.pillar_id;
        if (context.question_id) contextId += "/" + context.question_id;
        if (context.best_practice_id) contextId += "/" + context.best_practice_id;

        return contextId;
    }
}

export enum ScanProgressState
{
    Pending,
    InProgress,
    ErrorStarting,
    ErrorDuring,
    Complete
}

export class WafAssessmentRunExtended
{
    public readonly frameworkLookup: Lookup<WellArchitectedFrameworkExtended> = {};

    firstScanComplete: boolean;

    get score(): number
    {
        return UtilsService.mean(UtilsService.mapLookup(this.frameworkLookup, (key,
                                                                               value) => value.score, true)) || 0;
    }

    get progress(): number
    {
        return UtilsService.mean(UtilsService.mapLookup(this.frameworkLookup, (key,
                                                                               value) => value.progress, true)) || 0;
    }

    get triggeringScan(): boolean
    {
        if (!this.latestEntryExt?.model.background_job)
        {
            for (const frameworkId in this.frameworkLookup)
            {
                if (this.frameworkLookup[frameworkId].hasScanningPolicies) return true;
            }
        }

        return false;
    }

    get scanStatus(): ScanProgressState
    {
        return this.triggeringScan ? ScanProgressState.InProgress : this.latestEntryExt?.scanStatus;
    }

    get scanStatusDate(): Date
    {
        if (this.triggeringScan) return null;

        const dates = UtilsService.mapLookup(this.frameworkLookup, (key,
                                                                    value) => value.scanStatusDate?.valueOf(), true);

        return dates.length ? new Date(Math.max(...dates)) : this.latestEntryExt?.scanStatusDate;
    }

    get lastScan(): Date
    {
        const scanCompletions = UtilsService.mapLookup(this.frameworkLookup, (key,
                                                                              value) => value.lastScan?.valueOf(), true);
        return scanCompletions.length ? new Date(Math.max(...scanCompletions)) : null;
    }

    private m_status: Models.WellArchitectedFrameworkAssessmentRunStatus;
    get status(): Models.WellArchitectedFrameworkAssessmentRunStatus
    {
        return this.m_status ?? this.model.status;
    }

    set status(status: Models.WellArchitectedFrameworkAssessmentRunStatus)
    {
        this.m_status = status;
    }

    get inProgress(): boolean
    {
        return this.model.status === Models.WellArchitectedFrameworkAssessmentRunStatus.InProgress;
    }

    private m_latestEntryExt: WafAssessmentRunHistoryExtended;
    get latestEntryExt(): WafAssessmentRunHistoryExtended
    {
        return this.m_latestEntryExt;
    }

    set latestEntryExt(entryExt: WafAssessmentRunHistoryExtended)
    {
        const isNewEntry      = this.m_latestEntryExt?.model.sysId !== entryExt?.model.sysId;
        this.m_latestEntryExt = entryExt;

        if (isNewEntry || !this.m_latestEntryExt?.model.background_job) this.updateScanningPolicies();
    }

    private constructor(public readonly appDomain: AppDomainContext,
                        public readonly model: Models.WellArchitectedFrameworkAssessmentRun,
                        public readonly definitionExt: WafAssessmentDefinitionExtended,
                        public readonly versionExt: WafAssessmentDefinitionVersionExtended,
                        public readonly byEntry: boolean)
    {}

    public getBuilder(generateNew: boolean): Promise<WafAssessmentRunBuilder>
    {
        const fetcher = this.appDomain.wafFrameworks.getFrameworksFetcher(this.appDomain.users.lastActiveTenantId);
        if (generateNew) fetcher.resetHistoryCache(this.model.sysId);
        return fetcher.getAssessmentRunBuilder(this.appDomain, this.model);
    }

    public async ensureFrameworkExts()
    {
        await inParallel(this.versionExt.model.details.framework_ids, (frameworkId) => this.ensureFrameworkExt(frameworkId));
    }

    private buildFrameworkRun(builder: WafAssessmentRunBuilder,
                              frameworkId: string): Promise<WellArchitectedFrameworkExtended>
    {
        if (!builder || !frameworkId) return null;
        return this.byEntry ? builder.buildFrameworkRun(this, frameworkId) : builder.buildFrameworkRunByDay(this, frameworkId);
    }

    public async ensureFrameworkExt(frameworkId: string): Promise<boolean>
    {
        const builder = await this.getBuilder(false);

        let frameworkExt = this.frameworkLookup[frameworkId];
        if (frameworkId && !frameworkExt)
        {
            frameworkExt = await this.buildFrameworkRun(builder, frameworkId);
            if (frameworkExt) this.frameworkLookup[frameworkId] = frameworkExt;
        }
        return !!frameworkExt;
    }

    public updateScanningPolicies(context?: Models.WellArchitectedFrameworkAssessmentContext)
    {
        const contexts = context ? [context] : null;
        for (const frameworkId in this.frameworkLookup) this.frameworkLookup[frameworkId].updateScanningPolicies(contexts);
    }

    public overallMilestoneScore(milestone: string): number
    {
        return UtilsService.mean(UtilsService.mapLookup(this.frameworkLookup, (key,
                                                                               value) => value.scoresLookup[milestone]?.overallScore, true)) || 0;
    }

    public async scanUpdatesExist(): Promise<boolean>
    {
        this.appDomain.wafFrameworks.getFrameworksFetcher(this.appDomain.users.lastActiveTenantId)
            .resetHistoryCache(this.model.sysId);
        const runExt = await WafAssessmentRunExtended.newInstance(this.appDomain, this.model, this.definitionExt, this.versionExt, null);

        const latestEntry = runExt?.latestEntryExt?.model;
        const hasNewEntry = this.latestEntryExt?.model.sysId !== latestEntry?.sysId;

        return hasNewEntry || runExt.scanStatus !== this.scanStatus || !latestEntry?.background_job;
    }

    //--//

    private static async getAllHistory(appDomain: AppDomainContext,
                                       model: Models.WellArchitectedFrameworkAssessmentRun): Promise<WafAssessmentRunHistoryExtended[]>
    {
        const fetcher = appDomain.wafFrameworks.getFrameworksFetcher(appDomain.users.lastActiveTenantId);
        return fetcher.getAllHistory(appDomain, model.sysId);
    }

    public static async getLastHistoryEntry(appDomain: AppDomainContext,
                                            model: Models.WellArchitectedFrameworkAssessmentRun): Promise<WafAssessmentRunHistoryExtended>
    {
        const history = await WafAssessmentRunExtended.getAllHistory(appDomain, model);
        return history?.[history.length - 1];
    }

    public static async newInstance(appDomain: AppDomainContext,
                                    model: Models.WellArchitectedFrameworkAssessmentRun,
                                    definitionExt: WafAssessmentDefinitionExtended,
                                    versionExt: WafAssessmentDefinitionVersionExtended,
                                    lastHistoryEntryId: string): Promise<WafAssessmentRunExtended>
    {
        let lastEntryExt: WafAssessmentRunHistoryExtended;
        if (lastHistoryEntryId)
        {
            const history = await WafAssessmentRunExtended.getAllHistory(appDomain, model);
            lastEntryExt  = history?.find((entryExt) => entryExt.model.sysId === lastHistoryEntryId);
        }
        else
        {
            lastEntryExt = await WafAssessmentRunExtended.getLastHistoryEntry(appDomain, model);
        }

        const runExt          = new WafAssessmentRunExtended(appDomain, model, definitionExt, versionExt, true);
        runExt.latestEntryExt = lastEntryExt;
        await runExt.ensureFrameworkExts();
        runExt.updateScanningPolicies();

        return runExt;
    }

    public static async newInstanceByDay(appDomain: AppDomainContext,
                                         model: Models.WellArchitectedFrameworkAssessmentRun,
                                         definitionExt: WafAssessmentDefinitionExtended,
                                         versionExt: WafAssessmentDefinitionVersionExtended): Promise<WafAssessmentRunExtended>
    {
        const runExt = new WafAssessmentRunExtended(appDomain, model, definitionExt, versionExt, false);
        await runExt.ensureFrameworkExts();
        return runExt;
    }
}

interface WafAssessmentRunHistoryBucket
{
    id: string;
    questions: WafAssessmentRunQuestionExtended[];
}

interface WafOrganizedHistory
{
    bucketsByFramework: Lookup<WafAssessmentRunHistoryBucket[]>;
}

export class WafAssessmentRunHistoryExtended implements WafAssessmentRunHistoryBucket
{
    get id(): string
    {
        return this.model.sysId;
    }

    get scanStatus(): ScanProgressState
    {
        return this.model.background_job ? ScanProgressState.InProgress : ScanProgressState.Complete;
    }

    get scanStatusDate(): Date
    {
        switch (this.scanStatus)
        {
            case ScanProgressState.Complete:
                const dates = [];
                for (const scanExt of this.policyScanExts)
                {
                    const date = scanExt.model.completed || scanExt.model.started;
                    if (date) dates.push(date.valueOf());
                }

                if (dates.length) return new Date(Math.max(...dates));
            // fall through if no date info

            default:
                return this.model.createdOn;
        }
    }

    public readonly questions: WafAssessmentRunQuestionExtended[];
    public readonly policyScanExts: WafAssessmentRunPolicyScanExtended[];

    constructor(public readonly model: Models.WellArchitectedFrameworkAssessmentRunHistory,
                questions: Models.WellArchitectedFrameworkAssessmentRunQuestion[])
    {
        this.questions = questions?.sort((questionA,
                                          questionB) => MomentHelper.compareDates(questionA.createdOn, questionB.createdOn, true))
                                  .map((question) => new WafAssessmentRunQuestionExtended(question)) || [];

        this.policyScanExts = model.policy_scans?.map((scan) => new WafAssessmentRunPolicyScanExtended(model.sysId, scan)) || [];
    }
}

class WafHistoryByEntry implements WafOrganizedHistory
{
    public readonly bucketsByFramework: Lookup<WafAssessmentRunHistoryExtended[]>    = {};
    public readonly policyStates: Lookup<Lookup<WafAssessmentRunPolicyScanExtended>> = {};
    public readonly latestEntryByFramework: Lookup<string>                           = {};

    public readonly latestEntryId: string;

    constructor(frameworks: Models.WellArchitectedFramework[],
                public readonly history: WafAssessmentRunHistoryExtended[])
    {
        history ||= [];
        for (const framework of frameworks) this.bucketsByFramework[framework.framework_id] = [];

        const policyState: Lookup<WafAssessmentRunPolicyScanExtended> = {};
        for (const entry of history)
        {
            if (entry?.model?.triggered_contexts?.length)
            {
                const triggeredContext = entry.model.triggered_contexts;
                const isFirstScan      = triggeredContext.length == frameworks.length && triggeredContext.every(context => this.isFrameworkScan(context));
                const isReScan         = triggeredContext.length == 1 && this.isFrameworkScan(triggeredContext[0]);
                if (isFirstScan || isReScan)
                {
                    for (const context of triggeredContext)
                    {
                        this.latestEntryByFramework[context.framework_id] = entry.model.sysId;
                    }
                }
            }

            this.latestEntryId = entry.model.sysId;
            UtilsService.extractLookup(entry.policyScanExts, policyState);
            this.policyStates[this.latestEntryId] = {...policyState};

            for (const frameworkId in this.bucketsByFramework) this.bucketsByFramework[frameworkId].push(new WafAssessmentRunHistoryExtended(entry.model, []));

            for (const question of entry.questions)
            {
                const entryExts = this.bucketsByFramework[question.model.framework_id];
                if (entryExts)
                {
                    const latestEntry = entryExts[entryExts.length - 1];
                    latestEntry.questions.push(question);
                }
            }
        }
    }

    public isFrameworkScan(context: Models.WellArchitectedFrameworkAssessmentContext): boolean
    {
        return context.framework_id && !(context.pillar_id || context.question_id || context.best_practice_id);
    }
}

class WafAssessmentHistoryDayBucket implements WafAssessmentRunHistoryBucket
{
    get id(): string
    {
        return "" + this.dayStart;
    }

    public readonly questions: WafAssessmentRunQuestionExtended[] = [];

    constructor(public readonly dayStart: number)
    {}
}

class WafHistoryByDay implements WafOrganizedHistory
{
    public readonly bucketsByFramework: Lookup<WafAssessmentHistoryDayBucket[]> = {};

    constructor(history: WafAssessmentRunHistoryExtended[])
    {
        for (const entry of history || [])
        {
            for (const question of entry.questions)
            {
                const entryExts = LookupHelpers.ensureArray(this.bucketsByFramework, question.model.framework_id);
                const currDay   = WafAssessmentRunBuilder.getDayStart(question.model.createdOn);
                let latestEntry = entryExts[entryExts.length - 1];
                if (!latestEntry || currDay > latestEntry.dayStart)
                {
                    latestEntry = new WafAssessmentHistoryDayBucket(currDay);
                    entryExts.push(latestEntry);
                }

                latestEntry.questions.push(question);
            }
        }
    }
}

export class WafAssessmentRunBuilder
{
    private readonly m_organizedHistory: WafHistoryByEntry;
    private readonly m_builderLookup: Lookup<WafAssessmentRunFrameworkBuilder>;
    private readonly m_dayBuilderLookup: Lookup<WafAssessmentRunFrameworkBuilder>;

    constructor(frameworks: Models.WellArchitectedFramework[],
                history: WafAssessmentRunHistoryExtended[])
    {
        this.m_organizedHistory = new WafHistoryByEntry(frameworks, history);
        const builders          = frameworks.map((framework) => new WafAssessmentRunFrameworkBuilder(framework, this.m_organizedHistory.bucketsByFramework[framework.framework_id] || []));
        this.m_builderLookup    = UtilsService.extractLookup(builders);

        const dayHistory        = new WafHistoryByDay(history);
        const dayBuilders       = frameworks.map((framework) => new WafAssessmentRunFrameworkBuilder(framework, dayHistory.bucketsByFramework[framework.framework_id] || []));
        this.m_dayBuilderLookup = UtilsService.extractLookup(dayBuilders);
    }

    public buildFrameworkRun(runExt: WafAssessmentRunExtended,
                             frameworkId: string): Promise<WellArchitectedFrameworkExtended>
    {
        return this.m_builderLookup[frameworkId]?.build(runExt, this.m_organizedHistory);
    }

    public buildFrameworkRunByDay(runExt: WafAssessmentRunExtended,
                                  frameworkId: string): Promise<WellArchitectedFrameworkExtended>
    {
        return this.m_dayBuilderLookup[frameworkId]?.build(runExt, null);
    }

    public updateRun(runExt: WafAssessmentRunExtended)
    {
        runExt.latestEntryExt = this.m_organizedHistory.history.find((entryExt) => entryExt.model.sysId === this.m_organizedHistory.latestEntryId);
        for (const frameworkId in runExt.frameworkLookup)
        {
            this.m_builderLookup[frameworkId].updateFrameworkRun(runExt, runExt.frameworkLookup[frameworkId], this.m_organizedHistory);
        }
    }

    public appendQuestion(question: Models.WellArchitectedFrameworkAssessmentRunQuestion): boolean
    {
        return this.m_builderLookup[question.framework_id]?.appendQuestion(new WafAssessmentRunQuestionExtended(question));
    }

    public static getDayStart(d: Date): number
    {
        return MomentHelper.parse(d)
                           .startOf("day")
                           .valueOf();
    }
}

class WafAssessmentRunFrameworkBuilder
{
    get id(): string
    {
        return this.m_framework.framework_id;
    }

    private readonly m_questionLookup: Lookup<WafAssessmentRunQuestionExtended> = {};

    constructor(private readonly m_framework: Models.WellArchitectedFramework,
                private readonly m_buckets: WafAssessmentRunHistoryBucket[])
    {
        for (const bucket of this.m_buckets)
        {
            for (const question of bucket.questions)
            {
                this.m_questionLookup[question.id] = question;
            }
        }
    }

    public async build(runExt: WafAssessmentRunExtended,
                       historyByEntry: WafHistoryByEntry): Promise<WellArchitectedFrameworkExtended>
    {
        if (!runExt) return null;

        const frameworkExt  = await WellArchitectedFrameworkExtended.newInstance(runExt.appDomain, this.m_framework);
        frameworkExt.runExt = runExt;

        if (historyByEntry)
        {
            // need to updatePolicyState initially so that we can establish automated bps
            frameworkExt.updatePolicyState(historyByEntry.policyStates[historyByEntry.latestEntryId]);
        }

        this.updateFrameworkRun(runExt, frameworkExt, historyByEntry);

        return frameworkExt;
    }

    public updateFrameworkRun(runExt: WafAssessmentRunExtended,
                              frameworkExt: WellArchitectedFrameworkExtended,
                              historyByEntry: WafHistoryByEntry)
    {
        if (historyByEntry?.history?.length)
        {
            runExt.firstScanComplete ||= historyByEntry.history.length > 1 ||
                                         (runExt.latestEntryExt ? !runExt.latestEntryExt.model.background_job : !!historyByEntry.history[0]?.policyScanExts.length);

            const policyState: Lookup<WafAssessmentRunPolicyScanExtended> = {};
            let entryPoint                                                = false;
            for (const entryExt of historyByEntry.history)
            {
                if (entryExt.id === historyByEntry.latestEntryByFramework[frameworkExt.id])
                {
                    entryPoint = true;
                }
                if (entryPoint && entryExt.model.triggered_contexts?.length)
                {
                    for (const scan of entryExt.policyScanExts)
                    {
                        if (frameworkExt.hasPolicy(scan.model.policy_uri)) policyState[scan.id] = scan;
                    }
                }
            }

            frameworkExt.updatePolicyState(policyState);
        }

        for (const pillar of frameworkExt.pillars)
        {
            pillar.setBaseStates(runExt.definitionExt.model.owner);
        }

        for (const bucket of this.m_buckets)
        {
            for (const question of bucket.questions)
            {
                const questionExt = frameworkExt.getQuestion(question.model.pillar_id, question.model.question_id);
                if (questionExt)
                {
                    questionExt.processAnswers(question.model.answers);
                    questionExt.processQuestion(question);
                    questionExt.stateChanged();
                }
            }

            frameworkExt.bucketProcessed(bucket.id);

            if (runExt.latestEntryExt?.model.sysId === bucket.id) break;
        }
    }

    public appendQuestion(question: WafAssessmentRunQuestionExtended): boolean
    {
        const existingQuestion = this.m_questionLookup[question.id];
        if (existingQuestion)
        {
            existingQuestion.updateIfMoreRecent(question);
        }
        else
        {
            let lastEntryExt = this.m_buckets[this.m_buckets.length - 1];
            if (lastEntryExt.id !== question.model.assessment_run_history.sysId)
            {
                return false;
            }

            lastEntryExt.questions.push(question);
            this.m_questionLookup[question.id] = question;
        }

        return true;
    }
}
